/**
 * Thumbs up/down memory for the finished-video player. Stored per-browser in
 * localStorage so the buttons stay hidden for a video the user already rated;
 * a re-edit delivers a new file, which is a new key and can be rated again.
 */

const KEY = 'vivideo_v2_video_ratings';
const CAP = 500;

/** Only videos generated from here on ask for a rating (2026-09-21 11:00 UTC). */
const RATING_SINCE_MS = Date.parse('2026-09-21T11:00:00Z');

/** `approvedAt` is the order's last Generate, a UTC ISO string the backend writes without a zone. */
export function approvedAtMs(approvedAt?: string | null): number | null {
  if (!approvedAt) return null;
  const ms = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(approvedAt) ? approvedAt : `${approvedAt}Z`);
  return Number.isNaN(ms) ? null : ms;
}

export function isRateable(approvedAt?: string | null): boolean {
  const ms = approvedAtMs(approvedAt);
  return ms !== null && ms >= RATING_SINCE_MS;
}

type RatingMap = Record<string, 'like' | 'dislike'>;

/** Order id + the delivered file (query stripped — presigned urls change per open). */
export function videoRatingKey(orderId: string, fileUrl: string): string {
  let file = fileUrl || '';
  try { file = new URL(fileUrl, window.location.origin).pathname; } catch { /* keep the raw string */ }
  return `${orderId}::${file}`;
}

function readAll(): RatingMap {
  const raw = window.localStorage.getItem(KEY);
  if (!raw) return {};
  const parsed: unknown = JSON.parse(raw);
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as RatingMap) : {};
}

export function getVideoRating(key: string): 'like' | 'dislike' | null {
  // localStorage can throw in private windows — the rating memory is best-effort only.
  try { return readAll()[key] || null; } catch { return null; }
}

export function setVideoRating(key: string, rating: 'like' | 'dislike'): void {
  try {
    const all = readAll();
    delete all[key];
    all[key] = rating;
    // Insertion order is oldest-first, so dropping from the front keeps the newest CAP.
    const entries = Object.entries(all);
    const kept = entries.length > CAP ? entries.slice(entries.length - CAP) : entries;
    window.localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(kept)));
  } catch { /* best-effort */ }
}
